import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    agregarProcedimientos,
    deleteProcedimientoFactura,
    resetProcedimientoFactura,
} from "../../../redux/actions";
import { tituloClinico } from "../../../utils/textoClinico";
import { origenProcedimiento, montoLegible } from "../../../utils/facturaCrear";
import {
    alternar,
    alternarTodas,
    atajosPorTipo,
    estadoCabecera,
    idDe,
    idsDeTipo,
    importeSeleccionado,
    necesitaConfirmacion,
    podar,
    seleccionadas,
} from "../../../utils/facturaLote";
import BarraLote from "./BarraLote";
import Modal from "../../UI/Modal/Modal";
import { useToast } from "../../UI/Aviso";
import "../../../styles/FacturaCrear.css";

/* ============================================================================
   CREAR FACTURA · la tabla de procedimientos

   Sustituye a la <Table> de antd que tenía scroll horizontal incluso vacía
   (`scroll={{ x: "100%" }}`) y un botón rojo "Eliminar" repetido en cada fila.

   Rejilla de la referencia:
   `34px minmax(0,1fr) 112px 148px 100px 84px 96px 34px` con gap 12. El nombre
   es la única columna elástica: trunca con ellipsis y `title`, y por eso no
   hace falta scroll lateral.

   LA COLUMNA ORIGEN es la razón de ser de la disposición apilada: tres
   "BIOMETRIA HEMATICA COMPLETA" del mismo paciente son indistinguibles sin
   ella. Sobre qué se puede pintar ahí -- y qué NO existe en la base -- ver
   `origenProcedimiento` en utils/facturaCrear.js.

   NADA DE LÓGICA CAMBIA: quitar una línea y cambiar su cantidad despachan
   exactamente las mismas acciones de redux que despachaba GridFactura.

   FASE 2 -- selección y quitado en lote. Se podían agregar 380 procedimientos
   de un clic y había que quitarlos de uno en uno; esta es la mitad que
   faltaba. Tres cosas que NO cambian con ello:

   · Quitar sigue siendo `deleteProcedimientoFactura`, una por línea. No hay
     acción nueva en el reducer ni llamada al servidor: quitar de la factura es
     estado local hasta emitir, como siempre.
   · DESHACER se hace con las acciones que ya existen -- se guarda una foto del
     array ANTES de quitar y se restaura con reset + agregar. Restaura también
     el ORDEN, que un "volver a agregar" a secas perdería.
   · Quitar de la factura NO es lo mismo que eliminar el pendiente. El icono de
     papelera del modal de pendientes llama a PUT /billing/procedures/:id y
     marca la prestación "Eliminado" en la base, para siempre. Esto de aquí no
     toca la base. Los dos verbos deben seguir viéndose distintos.
   ========================================================================== */

function IconoQuitar() {
    return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="1.9" strokeLinecap="round" aria-hidden="true">
            <path d="M3 6h18" /><path d="M8 6V4h8v2" />
            <path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6" /><path d="M14 11v6" />
        </svg>
    );
}


/**
 * GridFactura hacía `record.precio.toFixed(2)` sin comprobar nada: un
 * procedimiento sin precio tumbaba la pantalla entera en vez de mostrar un
 * guion. No cambia ningún cálculo -- el número es el mismo cuando existe.
 */
/* -- copago -----------------------------------------------------------------
   Cuando se aplica un plan, la linea guarda un bloque `plan` con el reparto:
   `precio` de la linea pasa a ser lo que paga el PACIENTE, y `plan.precio` es
   lo que asume la ASEGURADORA. Hasta ahora eso solo se veia como una etiqueta
   "En copago" y una bajada de precio, sin decir cuanto asume quien.

   No se calcula nada aqui: los tres numeros ya estan en la linea (los pone
   `calcularMontosCobertura` al aplicar el plan, y el backend los recalcula
   igual al facturar). Esto solo los pinta.
   ------------------------------------------------------------------------- */
function copagoDe(p) {
    const plan = p?.plan;
    if (!plan) return null;
    const empresa = Number(plan.precio_empresa ?? plan.precio);
    const paciente = Number(plan.precio_paciente ?? p?.precio);
    const pct = Number(plan.copago);
    return {
        empresa: Number.isFinite(empresa) ? empresa : null,
        paciente: Number.isFinite(paciente) ? paciente : null,
        porcentaje: Number.isFinite(pct) ? pct : null,
    };
}

function precioDe(p) {
    // `Number(null)` es 0, así que un precio ausente pasaría por cero y se
    // pintaría "$0.00" -- en una factura, un cero inventado es peor que un
    // guion. Se comprueba la ausencia ANTES de convertir.
    const crudo = p?.precio;
    if (crudo === null || crudo === undefined || crudo === "") return null;
    const v = Number(crudo);
    return Number.isFinite(v) ? v : null;
}

function Fila({ procedimiento, indice, onQuitar, onCantidad, marcada, onMarcar, hayCopago }) {
    const nombre = tituloClinico(procedimiento?.ExamenTipo?.descripcion ?? "");
    const codigo = procedimiento?.ExamenTipo?.codigo ?? "";
    const tipo = procedimiento?.ExamenTipo?.tipo ?? "";
    const origen = origenProcedimiento(procedimiento);
    const precio = precioDe(procedimiento);
    const copago = copagoDe(procedimiento);
    const cantidad = Number(procedimiento?.cantidad) || 1;
    const total = precio == null ? null : precio * cantidad;

    return (
        <div className={`fcrear__tg fcrear__fila${marcada ? " is-marcada" : ""}`} role="row">
            <span className="fcrear__th--check">
                <input
                    type="checkbox"
                    className="fcrear__check"
                    checked={marcada}
                    onChange={() => onMarcar(procedimiento)}
                    aria-label={`Seleccionar ${nombre}`}
                />
            </span>
            <span className="fcrear__num">{indice + 1}</span>

            <div className="fcrear__proc">
                <span className="fcrear__proc-nombre" title={nombre}>{nombre}</span>
                <span className="fcrear__proc-meta">
                    {codigo && <span className="fcrear__codigo">{codigo}</span>}
                    {/* Las mismas dos etiquetas que ya pintaba GridFactura. */}
                    {procedimiento?.ExamenTipo?.contifico_id && (
                        <span className="fcrear__tag fcrear__tag--ok">En Contífico</span>
                    )}
                    {procedimiento?.plan && (
                        <span className="fcrear__tag fcrear__tag--aviso">En copago</span>
                    )}
                </span>
            </div>

            <span className="fcrear__celda" title={tipo}>{tipo}</span>

            <div className="fcrear__origen">
                {origen.fecha && <span className="fcrear__origen-fecha">{origen.fecha}</span>}
                {origen.referencia && (
                    <span className="fcrear__origen-ref">{origen.referencia}</span>
                )}
            </div>

            {/* Stepper en vez de un campo suelto. El mínimo es 1, igual que el
                `min={1}` del InputNumber de antes; no hay máximo hoy y aquí
                tampoco se inventa uno. */}
            <div className="fcrear__stepper">
                <button
                    type="button"
                    className="fcrear__step"
                    onClick={() => onCantidad(procedimiento, cantidad - 1)}
                    disabled={cantidad <= 1}
                    aria-label={`Una unidad menos de ${nombre}`}
                >−</button>
                <span className="fcrear__step-val" aria-live="polite">{cantidad}</span>
                <button
                    type="button"
                    className="fcrear__step"
                    onClick={() => onCantidad(procedimiento, cantidad + 1)}
                    aria-label={`Una unidad más de ${nombre}`}
                >+</button>
            </div>

            {/* Estas dos columnas solo existen si ALGUNA linea tiene plan: sin
                plan aplicado, la tabla es la de siempre. */}
            {hayCopago && (
                <span className="fcrear__celda fcrear__num-col fcrear__copago" title="Lo que asume la aseguradora">
                    {copago && copago.empresa != null ? `$${montoLegible(copago.empresa)}` : "—"}
                </span>
            )}
            {hayCopago && (
                <span className="fcrear__celda fcrear__num-col fcrear__copago-pct" title="Porcentaje que paga el paciente">
                    {copago && copago.porcentaje != null ? `${copago.porcentaje}%` : "—"}
                </span>
            )}

            {/* Con `$`, igual que Total y que la columna de la aseguradora.
                Sin el, esta cifra queda entre dos columnas con simbolo y se
                lee como si fuera otra clase de numero -- se notaba sobre todo
                al llamarse "Paciente" y tener "Aseguradora" al lado. Un precio
                ausente sigue siendo un guion, nunca un cero inventado. */}
            <span className="fcrear__celda fcrear__num-col">
                {precio == null ? montoLegible(precio) : `$${montoLegible(precio)}`}
            </span>
            <span className="fcrear__celda fcrear__num-col fcrear__total">
                {total == null ? "—" : `$${montoLegible(total)}`}
            </span>

            <button
                type="button"
                className="fcrear__quitar"
                onClick={() => onQuitar(procedimiento)}
                title={`Quitar ${nombre}`}
                aria-label={`Quitar ${nombre}`}
            >
                <IconoQuitar />
            </button>
        </div>
    );
}

export default function TablaProcedimientos({
    filtro = "",
    accionesVacio = null,
    // FASE 3 lo rellenará: aplicar plan sólo a lo seleccionado. Mientras no
    // llegue, el botón no se pinta (ver BarraLote).
    onAplicarPlanA = null,
}) {
    const dispatch = useDispatch();
    const { mostrar } = useToast();
    // El `|| []` iba pegado al `useSelector` y devolvía un array NUEVO en cada
    // render cuando el estado venía vacío, lo que hacía inútiles los `useMemo`
    // de abajo (se recalculaban siempre). Se estabiliza aquí, una vez.
    const procedimientosCrudos = useSelector((state) => state.facturaredux.procedimientos);
    const procedimientos = useMemo(() => procedimientosCrudos || [], [procedimientosCrudos]);

    // Trampa 2: la selección es un Set de ids, NUNCA un array de filas atado
    // al filtro visible. Buscar no puede alterar lo seleccionado.
    const [seleccion, setSeleccion] = useState(() => new Set());
    const [porQuitar, setPorQuitar] = useState(null); // null = sin confirmación abierta

    const texto = filtro.trim().toLocaleLowerCase("es");
    const visibles = texto
        ? procedimientos.filter((p) => {
              const d = String(p?.ExamenTipo?.descripcion ?? "").toLocaleLowerCase("es");
              const c = String(p?.ExamenTipo?.codigo ?? "").toLocaleLowerCase("es");
              return d.includes(texto) || c.includes(texto);
          })
        : procedimientos;

    // Mismas acciones que despachaba GridFactura, con los mismos payloads.
    const quitar = (p) => dispatch(deleteProcedimientoFactura(p.idprocedimiento_cita_paciente));
    const cambiarCantidad = (p, cantidad) => {
        if (cantidad < 1) return;
        dispatch({
            type: "UPDATE_CANTIDAD_PROCEDIMIENTO",
            payload: { id: p.idprocedimiento_cita_paciente, cantidad },
        });
    };

    /* -- selección en lote -------------------------------------------------- */

    // La factura puede cambiar por debajo (se descarta, se quita una línea a
    // mano, llega un lote nuevo): la selección se poda contra lo que existe de
    // verdad, o la barra diría "380 seleccionados" sobre una tabla vacía.
    const seleccionViva = useMemo(() => podar(seleccion, procedimientos), [seleccion, procedimientos]);
    const marcadas = seleccionadas(procedimientos, seleccionViva);
    const importe = importeSeleccionado(procedimientos, seleccionViva);
    const atajos = useMemo(() => atajosPorTipo(procedimientos), [procedimientos]);
    // Las columnas de copago aparecen solo si hay algun plan aplicado: sin
    // plan, la tabla es exactamente la de antes.
    const hayCopago = useMemo(() => procedimientos.some((p) => Boolean(p?.plan)), [procedimientos]);
    const cabecera = estadoCabecera(seleccionViva, visibles);

    const alternarUna = (p) => setSeleccion((s) => alternar(podar(s, procedimientos), idDe(p)));
    const alternarVisibles = () => setSeleccion((s) => alternarTodas(podar(s, procedimientos), visibles));
    // "Solo los de X" SUSTITUYE la selección; sumarla convertiría el atajo en
    // "además los de X", que no es lo que dice el botón.
    const soloDeTipo = (tipo) => setSeleccion(idsDeTipo(procedimientos, tipo));
    const limpiarSeleccion = () => setSeleccion(new Set());

    const quitarSeleccionadas = () => {
        // La foto se toma ANTES de tocar nada, y se restaura entera: así
        // deshacer devuelve también el orden original.
        const antes = procedimientos;
        const cuantas = marcadas.length;

        marcadas.forEach((p) => dispatch(deleteProcedimientoFactura(idDe(p))));
        setSeleccion(new Set());
        setPorQuitar(null);

        // Deshacer se ofrece SIEMPRE, se haya confirmado o no: es más barato y
        // más amable que una pregunta, y aquí no cuesta nada porque nada de
        // esto ha llegado todavía al servidor.
        mostrar({
            tipo: "exito",
            texto: `Se ${cuantas === 1 ? "quitó" : "quitaron"} ${cuantas} procedimiento${cuantas === 1 ? "" : "s"}`,
            accion: {
                label: "Deshacer",
                onClick: () => {
                    dispatch(resetProcedimientoFactura());
                    dispatch(agregarProcedimientos(antes));
                },
            },
        });
    };

    const pedirQuitar = () => {
        if (necesitaConfirmacion(marcadas.length)) setPorQuitar(marcadas.length);
        else quitarSeleccionadas();
    };

    if (procedimientos.length === 0) {
        return (
            <div className="fcrear__vacio">
                <p className="fcrear__vacio-titulo">Todavía no hay procedimientos</p>
                <p className="fcrear__vacio-texto">
                    Agrégalos desde los pendientes del paciente o búscalos en el catálogo.
                </p>
                {accionesVacio}
            </div>
        );
    }

    if (visibles.length === 0) {
        return (
            <div className="fcrear__vacio">
                <p className="fcrear__vacio-titulo">Ningún procedimiento coincide</p>
                <p className="fcrear__vacio-texto">
                    Hay {procedimientos.length} en la factura, pero ninguno con «{filtro}».
                </p>
            </div>
        );
    }

    return (
        <>
            <BarraLote
                cuantas={marcadas.length}
                importe={importe}
                atajos={atajos}
                onAtajo={soloDeTipo}
                onLimpiar={limpiarSeleccion}
                onQuitar={pedirQuitar}
                onAplicarPlan={onAplicarPlanA ? () => onAplicarPlanA(marcadas) : null}
            />

            <div className={`fcrear__tabla${hayCopago ? " fcrear__tabla--copago" : ""}`} role="table">
                <div className="fcrear__tg fcrear__cabecera" role="row">
                    <span className="fcrear__th fcrear__th--check">
                        <input
                            type="checkbox"
                            className="fcrear__check"
                            checked={cabecera === "todas"}
                            ref={(el) => {
                                // "Algunas" no es ni marcado ni vacío: el guion
                                // dice que hay selección pero no completa, y
                                // sólo se puede poner desde JS.
                                if (el) el.indeterminate = cabecera === "algunas";
                            }}
                            onChange={alternarVisibles}
                            aria-label={
                                cabecera === "todas"
                                    ? "Quitar la selección de los procedimientos visibles"
                                    : "Seleccionar los procedimientos visibles"
                            }
                            data-testid="factura-check-todos"
                        />
                    </span>
                    <span className="fcrear__th">#</span>
                    <span className="fcrear__th">Procedimiento</span>
                    <span className="fcrear__th">Tipo</span>
                    <span className="fcrear__th">Origen</span>
                    <span className="fcrear__th fcrear__th--centro">Cantidad</span>
                    {hayCopago && (
                        <span className="fcrear__th fcrear__th--der" title="Lo que asume la aseguradora">
                            Aseguradora
                        </span>
                    )}
                    {hayCopago && (
                        <span className="fcrear__th fcrear__th--der" title="Porcentaje que paga el paciente">
                            Copago
                        </span>
                    )}
                    <span className="fcrear__th fcrear__th--der">
                        {hayCopago ? "Paciente" : "PVP"}
                    </span>
                    <span className="fcrear__th fcrear__th--der">Total</span>
                    <span className="fcrear__th" />
                </div>

                {visibles.map((p) => (
                    <Fila
                        key={p.idprocedimiento_cita_paciente}
                        procedimiento={p}
                        indice={procedimientos.indexOf(p)}
                        onQuitar={quitar}
                        onCantidad={cambiarCantidad}
                        marcada={seleccionViva.has(idDe(p))}
                        onMarcar={alternarUna}
                        hayCopago={hayCopago}
                    />
                ))}
            </div>

            {/* Por debajo del umbral se quita y se ofrece deshacer. Por encima
                se pregunta ADEMÁS, porque quitar 380 líneas de golpe merece un
                segundo de pausa. */}
            <Modal
                abierto={porQuitar !== null}
                titulo="Quitar de la factura"
                destructivo
                onCerrar={() => setPorQuitar(null)}
                secundario={{ label: "Cancelar" }}
                primario={{ label: `Sí, quitar ${porQuitar}`, onClick: quitarSeleccionadas }}
            >
                <p>
                    Vas a quitar <b>{porQuitar}</b> procedimientos de esta factura, por un total de{" "}
                    <b>${montoLegible(importe)}</b>.
                </p>
                <p className="fcrear__nota">
                    No se borra nada del paciente: los procedimientos vuelven a quedar disponibles
                    para facturar. Podrás deshacerlo justo después.
                </p>
            </Modal>
        </>
    );
}
